import { useEffect, useRef } from 'react';
import { FIELD_PALETTE, timeOfDayFor } from '~/design-system/components/v1_5/Dashboard';
import { cn } from '~/lib/utils';
import styles from './HeroDustField.module.css';

/**
 * The dashboard's dust, loosed over the whole hero.
 *
 * `DashboardBackdrop` paints a band: two coloured sources just off the top corners, falling off down
 * the page. This is the same shader family — the same 3px cells, the same per-speck breathing, the
 * same time-of-day palette — with the sources set free: seven sites that fade up somewhere in the
 * hero, hold, fade out, and reappear somewhere else on their own clocks, which is how the morph
 * loader's wait state moves (Guri, 2026-09-04: "the same morphing thing as artifact generation, but
 * the style we have in the header, on the whole hero space"). The corner sources stay, quieter, so
 * the top edge still reads as the header's.
 *
 * One WebGL2 canvas, one rAF, no React state per frame. No WebGL2 → nothing is drawn and the page
 * is simply its ground. Reduced motion → one frame at rest.
 */

const VS = `#version 300 es
in vec2 p; void main(){ gl_Position = vec4(p,0.,1.); }`;

const FS = `#version 300 es
precision highp float;
out vec4 o;
uniform vec2 uRes; uniform float uT, uIntensity, uGrain, uCoverage, uTwinkle, uMotion, uSites;
uniform vec3 uL, uR, uGround;

/* The assembly. uWord is the wordmark's own alpha, uWordRect is where it sits in SCREEN space
   (x, y, w, h as fractions), uWordMix is how far the dust has gathered into it, and uFlipY is 1
   when the canvas is mirrored by CSS so the mark still reads the right way up.
   No backticks in here: the whole shader is a JS template literal. */
uniform sampler2D uWord; uniform vec4 uWordRect; uniform float uWordMix, uFlipY;

float hash(vec2 p){ p = fract(p*vec2(123.34,456.21)); p += dot(p,p+45.32); return fract(p.x*p.y); }
float hash1(float n){ return fract(sin(n)*43758.5453); }

/* The dashboard's blob, aspect-corrected so a site is round on a wide field. */
float blob(vec2 uv, vec2 c, float r){
  vec2 d = uv - c; d.x *= uRes.x/uRes.y; d.y *= 1.35;
  return exp(-dot(d,d)/(r*r)*2.1);
}

void main(){
  vec2 px = gl_FragCoord.xy;
  vec2 uv = vec2(px.x/uRes.x, (uRes.y - px.y)/uRes.y);   // 0 at the top, 1 at the foot
  float sway = sin(uT*0.05*uMotion)*0.025;

  // The header's two sources, at half weight, so the top edge stays the top edge.
  float f = 0.5*blob(uv, vec2(0.07+sway, -0.06), 0.30)
          + 0.45*blob(uv, vec2(0.93-sway, -0.04), 0.28);

  // The roaming sites. Each runs its own cycle: fade up over the first quarter, hold, fade out over
  // the last third, then reroll position and size for the next cycle. Nothing travels; things
  // appear and go, in different places, on different clocks.
  for (int i = 0; i < 9; i++) {
    if (float(i) >= uSites) break;
    float fi = float(i);
    float speed = 0.045 + 0.03*hash1(fi*7.1);
    float t = uT*speed*uMotion + hash1(fi*3.3);
    float cyc = floor(t);
    float ph = fract(t);
    vec2 c = vec2(hash1(fi*11.7 + cyc*5.1), hash1(fi*19.3 + cyc*9.7));
    c = mix(vec2(0.04, 0.02), vec2(0.96, 0.98), c);
    float r = mix(0.12, 0.30, hash1(fi*29.1 + cyc*3.3));
    float env = smoothstep(0.0, 0.28, ph) * (1.0 - smoothstep(0.62, 1.0, ph));
    float amp = 0.55 + 0.45*hash1(fi*41.0 + cyc);
    f += amp*env*blob(uv, c, r);
  }

  // The canvas ends at a fixed height, so the dust has to be gone before the edge is: thin it out
  // over the last stretch and the field dissolves into the ground instead of stopping dead.
  f *= 1.0 - smoothstep(0.66, 0.99, uv.y);

  /*
   * The word, mixed into the same density field the blobs write.
   *
   * Nothing travels — it cannot, the specks are a fixed grid and hash(cell) is a pure function of
   * position. What happens is that f stops being blobs and becomes the wordmark, so cells go out
   * where the mark is not and light up where it is. With the per-cell twinkle already running it
   * reads as the dust gathering.
   *
   * AFTER the edge fade on purpose: that fade exists to dissolve the blobs before the canvas ends,
   * and applying it to the mark too would eat whichever end of the word sat low in the field.
   */
  if (uWordMix > 0.001) {
    float sy = uFlipY > 0.5 ? 1.0 - uv.y : uv.y;      // screen-space v, whatever CSS did to us
    vec2 wuv = (vec2(uv.x, sy) - uWordRect.xy) / uWordRect.zw;
    float inside = step(0.0, wuv.x) * step(wuv.x, 1.0) * step(0.0, wuv.y) * step(wuv.y, 1.0);
    // Alpha, not luminance: the mark ships as dark ink on transparent, so its COVERAGE is the alpha
    // channel and reading colour would give a field that is zero everywhere the ink is solid.
    float wf = texture(uWord, wuv).a * inside;
    f = mix(f, wf, uWordMix);
  }

  vec2 cell = floor(vec2(px.x, uRes.y-px.y)/uGrain);
  float h  = hash(cell);
  float phs = hash(cell+17.3)*6.2831;
  float br = mix(1.0, 0.5+0.5*sin(uT*0.8*uMotion + phs), uTwinkle);
  float on = step(h, f*uCoverage*br);
  float a  = uIntensity*(0.35+0.65*f)*(0.55+0.45*hash(cell+3.7))*on*br;
  vec3 col = mix(uL, uR, clamp(uv.x,0.,1.));
  o = vec4(mix(uGround, col, clamp(a,0.,1.)), 1.0);
}`;

/** The dashboard's tuned dials (`DASHBOARD_FIELD`), plus how many sites roam. */
const FIELD = { intensity: 0.42, grain: 3, coverage: 0.72, twinkle: 0.92, motion: 2.15, sites: 7 };

/** #141415 — surface-1, the page's ground. The field is opaque and mixes the dust into this. */
const GROUND: [number, number, number] = [0x14 / 255, 0x14 / 255, 0x15 / 255];

/**
 * The assembly cycle, in seconds. Roam, gather, hold it, let go — then roam again.
 *
 * Loops rather than firing once on scroll, because the point of it is to be caught: a one-shot that
 * plays while the reader is still on the pricing table has happened and will not happen again.
 */
const WORD_CYCLE = { roam: 6, gather: 1.8, hold: 4, release: 2.2 };

/** Fallback placement (screen-space fractions) when no `wordAnchor` element is on the page. */
const WORD_FALLBACK = { w: 0.62, y: 0.34 };

/**
 * How much of the anchor's box the dust actually fills, about its centre.
 *
 * The anchor is the full-width wordmark, and at 1.0 the dust matched it exactly — which was far too
 * much. Specks read heavier than the SVG ever did: that asset is 4% ink, so at the same size the
 * dust version is an order of magnitude more present. Same box, less of it.
 *
 * 0.825 is 0.55 x 1.5 (Beka, 2026-09-05). Note this is the scale of the ANCHOR's box, not of what
 * you see: `contain` then fits the mark's own 3.203:1 inside that 4.794:1 box, which takes another
 * 0.668 off the width. So the drawn mark is 0.825 of the anchor's HEIGHT and 0.551 of its width.
 */
const WORD_SCALE = 0.825;

/** The wordmark's own proportions, so the rect never stretches it. */
const WORD_ASPECT = 1280 / 267;

export interface HeroDustFieldProps {
  className?: string;
  /**
   * Assemble the dust into this image every cycle. Its ALPHA is the shape, so a transparent PNG or
   * SVG is what belongs here — the ink's colour is irrelevant, the dust supplies its own.
   *
   * Omit it and the field is only ever dust, which is what the hero wants.
   */
  wordSrc?: string;
  /**
   * CSS selector for the element the word should land on, measured every frame. Registers the dust
   * against something the page already lays out — the wordmark it is standing in for — instead of a
   * hardcoded rect that drifts the moment the section's padding changes. Falls back to
   * `WORD_FALLBACK` when nothing matches.
   */
  wordAnchor?: string;
  /** The canvas is mirrored by CSS (`scaleY(-1)`), so the mark has to be sampled the other way up. */
  flipped?: boolean;
}

export function HeroDustField({ className, wordSrc, wordAnchor, flipped = false }: HeroDustFieldProps) {
  const ref = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const gl = node.getContext('webgl2', { antialias: false, alpha: false });
    if (!gl) return;

    const compile = (type: number, source: string): WebGLShader | null => {
      const shader = gl.createShader(type);
      if (!shader) return null;
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      if (gl.getShaderParameter(shader, gl.COMPILE_STATUS)) return shader;
      console.error('[HeroDustField]', gl.getShaderInfoLog(shader));
      return null;
    };
    const program = gl.createProgram();
    const vs = compile(gl.VERTEX_SHADER, VS);
    const fs = compile(gl.FRAGMENT_SHADER, FS);
    if (!program || !vs || !fs) return;
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error('[HeroDustField]', gl.getProgramInfoLog(program));
      return;
    }
    gl.useProgram(program);

    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const position = gl.getAttribLocation(program, 'p');
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);

    const uniforms = new Map<string, WebGLUniformLocation | null>();
    const u = (name: string) => {
      if (!uniforms.has(name)) uniforms.set(name, gl.getUniformLocation(program, name));
      return uniforms.get(name) ?? null;
    };

    let cssWidth = 0;
    let cssHeight = 0;
    let dpr = 1;
    const resize = () => {
      const width = node.clientWidth;
      const height = node.clientHeight;
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      if (width === cssWidth && height === cssHeight && ratio === dpr) return;
      cssWidth = width;
      cssHeight = height;
      dpr = ratio;
      node.width = Math.round(width * ratio);
      node.height = Math.round(height * ratio);
      gl.viewport(0, 0, node.width, node.height);
    };

    /*
     * The wordmark, as a texture. Loaded once; until it arrives `uWordMix` stays pinned at 0, so the
     * field is simply dust and nothing pops.
     */
    const wordTex = gl.createTexture();
    let wordReady = false;
    let disposed = false;
    /** The mark's own proportions, learned from the file rather than assumed. See `wordRect`. */
    let wordAspect = 0;

    /*
     * The mark, forced SOLID before it becomes a texture.
     *
     * `logo-visual.svg` is not a flat wordmark: its four paths are filled with gradients that run
     * from `stop-opacity="0.04"` to `0`. Rasterised as shipped it is 99% transparent — mean alpha 2
     * out of 255 — so sampling it would extinguish the dust into an empty rectangle rather than
     * assemble it into anything. Swapping the four `fill="url(#…)"` for solid white turns the same
     * paths into a clean 44%-coverage mask of the LETTERFORMS, which is what the field needs: the
     * dust supplies its own colour and its own falloff, and the asset only has to say where the
     * letters are.
     */
    const wordUrl = async (): Promise<string> => {
      if (!/\.svg(\?|$)/i.test(wordSrc ?? '')) return wordSrc ?? '';
      const text = await fetch(wordSrc as string).then((r) => r.text());
      const solid = text.replace(/fill="url\(#[^)]*\)"/g, 'fill="#ffffff"');
      return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(solid)}`;
    };

    if (wordSrc && wordTex) {
      const image = new Image();
      // Same-origin asset; the flag keeps the texture usable if it ever moves to a CDN.
      image.crossOrigin = 'anonymous';
      image.onload = () => {
        if (disposed) return;
        gl.bindTexture(gl.TEXTURE_2D, wordTex);
        /*
         * NOT flipped on upload, and that is the whole point.
         *
         * The tempting move is `UNPACK_FLIP_Y_WEBGL = true`, because GL's texture origin is usually
         * described as bottom-left. But this shader does not sample in GL space: `wuv.y` is measured
         * DOWN from the top of the word's rect on screen, already corrected for the CSS mirror by
         * `uFlipY`. Left at the default the image's first row lands at v=0, which is exactly that
         * top — so the mark reads upright. Flipping on upload put v=0 at the image's bottom and drew
         * the whole word upside down (Beka caught it: 2026-09-05).
         */
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
        // CLAMP + LINEAR, no mips: the mark is sampled at roughly its own size and never tiles.
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        wordAspect = image.naturalHeight > 0 ? image.naturalWidth / image.naturalHeight : 0;
        wordReady = true;
      };
      image.onerror = () => console.warn('[HeroDustField] wordmark did not load:', wordSrc);
      void wordUrl()
        .then((url) => {
          if (!disposed) image.src = url;
        })
        .catch((error) => console.warn('[HeroDustField] wordmark could not be prepared:', error));
    }

    /**
     * Letterbox a rect down to the mark's own proportions — `object-fit: contain`, in UV.
     *
     * The anchor is an `<img>` whose CSS box is `aspect-ratio: 1280 / 267` while this file's viewBox
     * is `740 / 231`. The img hides that with `object-fit: contain`; the shader does not, so mapping
     * the texture across the full box stretched the wordmark 50% wider than it is drawn (Beka spotted
     * it: 2026-09-05). Fitting here rather than hardcoding a ratio is also what lets the SVG be
     * swapped for one of any shape without the dust distorting it.
     */
    const contain = (
      x: number,
      y: number,
      w: number,
      h: number,
    ): [number, number, number, number] => {
      if (wordAspect <= 0 || cssWidth <= 0 || cssHeight <= 0) return [x, y, w, h];
      const rectAspect = (w * cssWidth) / (h * cssHeight);
      if (rectAspect <= 0) return [x, y, w, h];
      if (wordAspect > rectAspect) {
        const nh = h * (rectAspect / wordAspect);   // too wide for the box: pillar the height
        return [x, y + (h - nh) / 2, w, nh];
      }
      const nw = w * (wordAspect / rectAspect);     // too tall: letterbox the width
      return [x + (w - nw) / 2, y, nw, h];
    };

    /** Where the mark sits, in screen-space fractions of the canvas. */
    const wordRect = (): [number, number, number, number] => {
      const anchor = wordAnchor ? document.querySelector(wordAnchor) : null;
      const canvasRect = node.getBoundingClientRect();
      if (anchor && canvasRect.width > 0 && canvasRect.height > 0) {
        const a = anchor.getBoundingClientRect();
        if (a.width > 0 && a.height > 0) {
          const w = (a.width / canvasRect.width) * WORD_SCALE;
          const h = (a.height / canvasRect.height) * WORD_SCALE;
          // Shrunk about the anchor's centre, so the mark stays where the layout put it.
          const cx = (a.left - canvasRect.left) / canvasRect.width + a.width / canvasRect.width / 2;
          const cy = (a.top - canvasRect.top) / canvasRect.height + a.height / canvasRect.height / 2;
          return contain(cx - w / 2, cy - h / 2, w, h);
        }
      }
      const w = WORD_FALLBACK.w;
      const h = cssHeight > 0 ? (w * cssWidth) / WORD_ASPECT / cssHeight : 0.2;
      return contain((1 - w) / 2, WORD_FALLBACK.y, w, h);
    };

    /** 0 = dust, 1 = fully assembled. Eased at both ends so neither edge snaps. */
    const wordMix = (elapsed: number): number => {
      if (!wordReady) return 0;
      const { roam, gather, hold, release } = WORD_CYCLE;
      const span = roam + gather + hold + release;
      const t = elapsed % span;
      if (t < roam) return 0;
      if (t < roam + gather) {
        const p = (t - roam) / gather;
        return p * p * (3 - 2 * p);
      }
      if (t < roam + gather + hold) return 1;
      const p = (t - roam - gather - hold) / release;
      return 1 - p * p * (3 - 2 * p);
    };

    /*
     * The ground is the canvas's OWN background colour, read from CSS.
     *
     * `.field` already paints `--color-surface-1` there, so the opaque shader and the element it
     * covers agreed only because the same hex was written in two places. Reading it means a variant
     * that wants another tone — the privacy plate sits a tone up — says so in CSS next to every
     * other colour on this page, and the shader follows. `GROUND` stays the fallback.
     */
    const cssGround = (): [number, number, number] => {
      const m = /rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)/.exec(
        getComputedStyle(node).backgroundColor,
      );
      return m ? [+m[1] / 255, +m[2] / 255, +m[3] / 255] : GROUND;
    };
    const ground = cssGround();

    const still = window.matchMedia('(prefers-reduced-motion: reduce)');
    const started = performance.now();
    let raf = 0;

    const draw = (now: number) => {
      resize();
      const palette = FIELD_PALETTE[timeOfDayFor()];
      gl.uniform2f(u('uRes'), node.width, node.height);
      gl.uniform1f(u('uT'), still.matches ? 0 : (now - started) / 1000);
      gl.uniform1f(u('uIntensity'), FIELD.intensity);
      gl.uniform1f(u('uGrain'), FIELD.grain * dpr);
      gl.uniform1f(u('uCoverage'), FIELD.coverage);
      gl.uniform1f(u('uTwinkle'), FIELD.twinkle);
      gl.uniform1f(u('uMotion'), FIELD.motion);
      gl.uniform1f(u('uSites'), FIELD.sites);
      gl.uniform3f(u('uL'), palette.left[0] / 255, palette.left[1] / 255, palette.left[2] / 255);
      gl.uniform3f(u('uR'), palette.right[0] / 255, palette.right[1] / 255, palette.right[2] / 255);
      gl.uniform3f(u('uGround'), ground[0], ground[1], ground[2]);

      // Reduced motion holds the mark assembled rather than dropping it: the finale still reads, it
      // simply does not perform. Same call the rest of this component makes with `uT`.
      const elapsed = (now - started) / 1000;
      const mix = wordReady ? (still.matches ? 1 : wordMix(elapsed)) : 0;
      gl.uniform1f(u('uWordMix'), mix);
      gl.uniform1f(u('uFlipY'), flipped ? 1 : 0);
      if (mix > 0.001 && wordTex) {
        const [rx, ry, rw, rh] = wordRect();
        gl.uniform4f(u('uWordRect'), rx, ry, rw, rh);
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, wordTex);
        gl.uniform1i(u('uWord'), 0);
      }
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    };

    const loop = (now: number) => {
      draw(now);
      raf = requestAnimationFrame(loop);
    };

    if (still.matches) {
      draw(performance.now());
      // The texture arrives after that single frame, so draw once more when it does — otherwise a
      // reduced-motion reader gets the dust and never the mark.
      if (wordSrc) window.setTimeout(() => still.matches && draw(performance.now()), 400);
    } else raf = requestAnimationFrame(loop);

    const onVisibility = () => {
      cancelAnimationFrame(raf);
      if (!document.hidden && !still.matches) raf = requestAnimationFrame(loop);
    };
    document.addEventListener('visibilitychange', onVisibility);
    const onResize = () => {
      if (still.matches) draw(performance.now());
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('resize', onResize);
      disposed = true;
      if (wordTex) gl.deleteTexture(wordTex);
    };
  }, [wordSrc, wordAnchor, flipped]);

  return <canvas ref={ref} className={cn(styles.field, className)} aria-hidden />;
}

HeroDustField.displayName = 'HeroDustField';
